import { useCallback, useEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { PickersInputComponentLocaleText } from '@mui/x-date-pickers/locales';
import { svSE } from '@mui/x-date-pickers/locales';
import { CalendarMonth } from '@mui/icons-material';
import { useDeviceCapabilities } from '@/hooks/useDeviceCapabilities';

/** Swedish texts for the calendar, its buttons and the screen-reader labels.
 *  The cast only reconciles the locale's `?: string | undefined` fields with
 *  this project's `exactOptionalPropertyTypes`. */
const SV_LOCALE_TEXT = svSE.components.MuiLocalizationProvider.defaultProps
  .localeText as PickersInputComponentLocaleText;

/**
 * Touch targets: 48px on touch devices, MUI's own 56px elsewhere. The medium
 * outlined field is 16.5px of section padding per side; this padding gives 48.
 */
const TOUCH_SECTION_PADDING_Y = '12.5px';

/**
 * MUI publishes a value after every keystroke in the year section, so an
 * edit from 2026 to 2027 passes through the years 2, 20 and 202. Those are
 * valid Dates but not what anyone meant; a year this short is still being
 * typed.
 */
const MIN_COMPLETE_YEAR = 1000;

/** What a form says next to a field the user left half typed or impossible. */
export const INCOMPLETE_DATE_MESSAGE = 'Ange ett fullständigt datum eller lämna fältet tomt';

/** A value worth reporting: a valid day with a fully typed year. */
function isComplete(date: Date): boolean {
  return !Number.isNaN(date.getTime()) && date.getFullYear() >= MIN_COMPLETE_YEAR;
}

/** Same instant, or both null. Two invalid dates count as equal so a parent
 *  seeding one (`new Date(badString)`) is not adopted over and over. */
function sameInstant(a: Date | null, b: Date | null): boolean {
  if (a === null || b === null) return a === b;
  const ta = a.getTime();
  const tb = b.getTime();
  return ta === tb || (Number.isNaN(ta) && Number.isNaN(tb));
}

export interface AppDatePickerProps {
  /**
   * The chosen day, or null. Only the calendar day is meaningful: the time
   * of day is whatever the parent seeded (a backend instant gives 01:00 or
   * 02:00 local) and comes back unchanged on every `onChange`.
   */
  value: Date | null;
  /**
   * Called with a valid Date on the chosen day whenever the field holds a
   * complete one, and with null whenever it does not: cleared, a section
   * cleared, a year still being typed, or an impossible date. Never called
   * twice in a row with the same value. Serialise with `toDateOnlyString`,
   * never `toISOString`.
   */
  onChange: (value: Date | null) => void;
  /**
   * Called when the field starts or stops holding something that is not a
   * day: a half-typed year, an impossible day, a year outside 1900–2099, or
   * some sections filled and others empty. The parent holds null meanwhile
   * and cannot tell that from an empty field, so an optional field that must
   * not be submitted half typed reads this instead. Reported false again when
   * the field is remounted with a real value or unmounted.
   */
  onInvalidChange?: (invalid: boolean) => void;
  /**
   * Called when the calendar popper or the mobile dialog closes, after any
   * `onChange` the pick caused. Together with `onBlur` this is where a parent
   * that saves on commit persists; the two can fire for one entry, so such a
   * parent dedupes. MUI's own `onAccept` is not a commit signal — it also
   * fires for every complete value typed.
   */
  onClose?: () => void;
  /** Visible floating label. Omit only when `ariaLabelledBy` names the field. */
  label?: string | undefined;
  /**
   * Id of the visible text that names an unlabelled field. Applied to the
   * input and to the calendar popper/dialog so both announce the same name.
   */
  ariaLabelledBy?: string;
  helperText?: string | undefined;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  /** Tints an empty field like `AgronodTextField emptyStyle="highlighted"`. */
  emptyHighlighted?: boolean;
  /** Full width by default; false renders MUI's intrinsic width. */
  fullWidth?: boolean;
  onFocus?: () => void;
  /**
   * Called when the field loses focus. An incomplete date is left as the user
   * typed it: the field shows the partial or impossible value (with MUI's
   * error) and the parent holds null. Nothing is ever restored or invented
   * on blur; a "restore the last complete day" once turned a mistyped 31
   * April into a silently saved 3 April.
   */
  onBlur?: () => void;
  /** Extra styles for the text field root. */
  sx?: SxProps<Theme>;
}

/**
 * The one date picker for the whole app (AG-285).
 *
 * Wraps MUI X DatePicker so every date field shares one behaviour: Swedish
 * texts, a calendar that only closes on selection, Escape or a click outside,
 * the modal dialog on touch devices (decided by pointer type, not viewport
 * width) and a 48px input there. The value is a native Date on the chosen
 * day, matching the rest of the app's date convention.
 *
 * The field is driven by a draft that mirrors MUI exactly. MUI relies on
 * seeing its own emissions echoed back as `value`: a half-typed year comes
 * out as a valid Date (year 2, 20, 202), an impossible date as an Invalid
 * Date, a cleared section as null, and each of those must go straight back
 * in or MUI wipes the other sections or hides its error state. The parent
 * only ever sees null or a complete day; its `value` is adopted when it
 * moves away from what was last reported (a refetch, a rollback), and the
 * inner picker is remounted on adoption: MUI's Avbryt reverts to the value
 * it mounted with (`lastCommittedValue` only follows accepts), so without
 * the remount a cancel on a phone would revert to a stale value or to the
 * null a row mounts with before its answer has loaded.
 */
export default function AppDatePicker({
  value,
  onChange,
  onInvalidChange,
  onClose,
  label,
  ariaLabelledBy,
  helperText,
  error = false,
  disabled = false,
  required = false,
  size = 'medium',
  emptyHighlighted = false,
  fullWidth = true,
  onFocus,
  onBlur,
  sx,
}: AppDatePickerProps) {
  const { isTouch } = useDeviceCapabilities();
  const [draft, setDraft] = useState<Date | null>(value);
  const reported = useRef<Date | null>(value);
  // MUI's own verdict on the draft (impossible day, year out of range).
  const [muiError, setMuiError] = useState(false);
  // The hidden input: the way to the rendered sections without a wrapper div.
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const invalidReported = useRef(false);
  const onInvalidChangeRef = useRef(onInvalidChange);
  onInvalidChangeRef.current = onInvalidChange;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const muiErrorRef = useRef(muiError);
  muiErrorRef.current = muiError;
  // Bumped on every adoption so MUI's cancel target follows the parent. A
  // change arriving while the popper or dialog is open discards it, tapped
  // day included; accepted, since it needs the server value to change under
  // an open picker.
  const [mountKey, setMountKey] = useState(0);

  // An external change is one the parent did not get from us.
  useEffect(() => {
    if (!sameInstant(value, reported.current)) {
      reported.current = value;
      setDraft(value);
      // The fresh instance starts from "no error" and only reports changes,
      // so a stale error must be forgotten here.
      setMuiError(false);
      setMountKey((key) => key + 1);
    }
  }, [value]);

  /**
   * Invalid means: MUI flags the draft, the year is short, or the value is
   * null while some sections still show digits (a cleared year, a month and
   * day typed into an empty field). MUI never reports the last case through
   * `onError`, so the rendered sections are read directly.
   */
  const reportInvalid = useCallback(() => {
    const draftNow = draftRef.current;
    const sections = hiddenInputRef.current
      ?.closest('.MuiPickersInputBase-root')
      ?.querySelectorAll('[role="spinbutton"]');
    const partlyFilled =
      draftNow === null &&
      Array.from(sections ?? []).some((s) => s.getAttribute('aria-valuenow') !== null);
    const invalid =
      muiErrorRef.current || (draftNow !== null && !isComplete(draftNow)) || partlyFilled;
    if (invalid === invalidReported.current) return;
    invalidReported.current = invalid;
    onInvalidChangeRef.current?.(invalid);
  }, []);

  useEffect(reportInvalid, [reportInvalid, draft, muiError, mountKey]);

  // Sections change without a value change while a field is being filled in;
  // watch them rather than guess which events cover it.
  useEffect(() => {
    const root = hiddenInputRef.current?.closest('.MuiPickersInputBase-root');
    if (!root) return undefined;
    const observer = new MutationObserver(reportInvalid);
    observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['aria-valuenow'] });
    return () => observer.disconnect();
  }, [reportInvalid, mountKey]);

  // A field that goes away is not invalid any more (the create page unmounts
  // it when the control type changes).
  useEffect(
    () => () => {
      if (invalidReported.current) onInvalidChangeRef.current?.(false);
    },
    [],
  );

  const handleChange = (next: Date | null) => {
    setDraft(next);
    const report = next !== null && isComplete(next) ? next : null;
    if (sameInstant(report, reported.current)) return;
    reported.current = report;
    onChange(report);
  };

  const highlight = emptyHighlighted && draft === null && !error && !disabled;

  return (
    <DatePicker
      key={mountKey}
      value={draft}
      onChange={handleChange}
      onError={(reason) => setMuiError(reason !== null)}
      inputRef={hiddenInputRef}
      {...(onClose && { onClose })}
      {...(label && { label })}
      disabled={disabled}
      localeText={SV_LOCALE_TEXT}
      // MUI's default; stated so nobody swaps it for a breakpoint. A wide
      // iPad has a coarse pointer and must get the modal variant.
      desktopModeMediaQuery="@media (pointer: fine)"
      slots={{ openPickerIcon: CalendarMonth }}
      slotProps={{
        textField: {
          fullWidth,
          required,
          // Only ever true: a false prop would override MUI's own error for
          // a half-typed or impossible date.
          ...(error && { error }),
          size,
          ...(helperText !== undefined && { helperText }),
          ...(onFocus && { onFocus }),
          ...(onBlur && { onBlur }),
          // MUI names the focusable group and the popper from `label` on its
          // own; an unlabelled field gets both from the external text.
          ...(ariaLabelledBy && {
            slotProps: { input: { 'aria-labelledby': ariaLabelledBy } },
          }),
          sx: [
            (theme) => ({
              ...(isTouch && size === 'medium' && {
                '& .MuiPickersInputBase-sectionsContainer': {
                  paddingTop: TOUCH_SECTION_PADDING_Y,
                  paddingBottom: TOUCH_SECTION_PADDING_Y,
                },
              }),
              ...(highlight && {
                '& .MuiPickersInputBase-root:not(.Mui-disabled)': {
                  backgroundColor: theme.palette.secondary.pastel,
                },
              }),
            }),
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ],
        },
        ...(ariaLabelledBy && {
          popper: { 'aria-labelledby': ariaLabelledBy },
          mobilePaper: { 'aria-labelledby': ariaLabelledBy },
        }),
      }}
    />
  );
}
